import {
  Icp,
  ICP_NO_ICP,
  ICP_REGISTER_STEP,
  IcpProduct,
  type IcpProductValue,
  type IcpRegisterFlow,
  type IcpRegisterPendingStep,
  type IcpRegisterVariantIcp,
  type IcpRegisterVariantProduct,
  type IcpValue,
} from '@/micro/types/register.type'
import { getQueryParameterByName } from './urls'

const RECOMMENDATION_ICPS: IcpValue[] = [Icp.SMALL_BUSINESS, Icp.MID_BUSINESS]
const DIRECT_REDIRECT_ICPS: IcpValue[] = [Icp.ACCOUNTANT, Icp.STUDENT]

const VALID_PRODUCTS_BY_ICP: Record<IcpValue, IcpProductValue[]> = {
  [Icp.SMALL_BUSINESS]: [IcpProduct.POS, IcpProduct.SF],
  [Icp.MID_BUSINESS]: [IcpProduct.POS, IcpProduct.AC, IcpProduct.NOMINA],
  [Icp.ACCOUNTANT]: [],
  [Icp.STUDENT]: [],
}

export const isIcpValue = (value: string): value is IcpValue =>
  Object.values(Icp).includes(value as IcpValue)

export const isIcpProductValue = (value: string): value is IcpProductValue =>
  Object.values(IcpProduct).includes(value as IcpProductValue)

const getRawQueryParam = (name: string, location?: string): string | null => {
  const href = location ?? (typeof window !== 'undefined' ? window.location.href : '')
  if (!href) return null

  try {
    const url = new URL(href)

    for (const [key, value] of url.searchParams.entries()) {
      if (key.toLowerCase() === name.toLowerCase()) {
        const trimmed = value.trim()
        return trimmed.length > 0 ? trimmed : null
      }
    }
  } catch {
    const legacy = getQueryParameterByName(name, location)
    if (legacy === null || legacy === '') return null
    return legacy.trim()
  }

  return null
}

const normalizeQueryToken = (value: string): string => value.trim().toUpperCase()

const getRawIcpFromQuery = (location?: string): string | null =>
  getRawQueryParam('icp', location)

const getRawProductFromQuery = (location?: string): string | null =>
  getRawQueryParam('product', location)

export const getIcpFromQuery = (location?: string): IcpValue | null => {
  const raw = getRawIcpFromQuery(location)
  if (!raw) return null
  const normalized = normalizeQueryToken(raw)
  return isIcpValue(normalized) ? normalized : null
}

export const isNominaRegisterPath = (location?: string): boolean => {
  const href = location ?? (typeof window !== 'undefined' ? window.location.href : '')
  if (!href) return false

  try {
    return /\/user\/register\/nomina(\/|$)/i.test(new URL(href).pathname)
  } catch {
    return /\/user\/register\/nomina(\/|$)/i.test(href)
  }
}

export const getProductFromQuery = (location?: string): IcpProductValue | null => {
  const raw = getRawProductFromQuery(location)
  if (raw) {
    const normalized = normalizeQueryToken(raw)
    if (isIcpProductValue(normalized)) return normalized
  }

  if (isNominaRegisterPath(location)) return IcpProduct.NOMINA

  return null
}

export const isValidProductForIcp = (icp: IcpValue, product: string): boolean =>
  VALID_PRODUCTS_BY_ICP[icp].includes(product as IcpProductValue)

/** Quita product cuando no aplica: ACCOUNTANT/STUDENT o combo icp+product inválido. */
export const shouldStripProductFromQuery = (icp: IcpValue, product: string | null): boolean => {
  if (!product) return false
  if (DIRECT_REDIRECT_ICPS.includes(icp)) return true
  return !isValidProductForIcp(icp, product)
}

export type SalvagedIcpRegisterQuery = {
  icp: IcpValue | null
  product: IcpProductValue | null
  /** Query normalizado cuando hay que limpiar params inválidos o combos imposibles. */
  replaceQuery: Record<string, string> | null
  isFullyInvalid: boolean
}

/**
 * Normaliza icp/product en URL:
 * - Completamente inválido → redirect limpio.
 * - icp válido + product ilegible o incompatible → conserva icp.
 * - product válido + icp ilegible → conserva product.
 */
export const salvageIcpRegisterQuery = (location?: string): SalvagedIcpRegisterQuery => {
  const rawIcp = getRawIcpFromQuery(location)
  const rawProduct = getRawProductFromQuery(location)
  let icp = getIcpFromQuery(location)
  let product = getProductFromQuery(location)

  const hasInvalidIcp = Boolean(rawIcp && !icp)
  const hasInvalidProduct = Boolean(rawProduct && !product)

  if (hasInvalidIcp && hasInvalidProduct) {
    return { icp: null, product: null, replaceQuery: null, isFullyInvalid: true }
  }

  if (hasInvalidIcp && !rawProduct) {
    return { icp: null, product: null, replaceQuery: null, isFullyInvalid: true }
  }

  if (hasInvalidProduct && !rawIcp) {
    return { icp: null, product: null, replaceQuery: null, isFullyInvalid: true }
  }

  let replaceQuery: Record<string, string> | null = null

  if (icp && rawProduct && !product) {
    replaceQuery = { icp }
    product = null
  } else if (product && rawIcp && !icp) {
    replaceQuery = { product }
    icp = null
  } else if (icp && product && shouldStripProductFromQuery(icp, product)) {
    if (isNominaRegisterPath(location) && !rawProduct) {
      replaceQuery = {}
      icp = null
    } else {
      replaceQuery = { icp }
      product = null
    }
  }

  return { icp, product, replaceQuery, isFullyInvalid: false }
}

/** Valor de icp/product ilegible sin parte válida que salvar. Combos inválidos no entran aquí. */
export const hasInvalidIcpRegisterQueryParams = (location?: string): boolean =>
  salvageIcpRegisterQuery(location).isFullyInvalid

export const inferIcpFromProductOnly = (product: IcpProductValue): IcpValue | null => {
  if (product === IcpProduct.SF) return Icp.SMALL_BUSINESS
  if (product === IcpProduct.AC) return Icp.MID_BUSINESS
  return null
}

export const getIcpRegisterFlowFromParams = (
  icp: IcpValue | null,
  product: IcpProductValue | null,
): Exclude<IcpRegisterFlow, 'invalid'> => {
  if (product && icp && isValidProductForIcp(icp, product)) return 'withProduct'
  if (icp) return 'withIcpOnly'
  if (product) return 'productOnly'
  return 'default'
}

export const resolveIcpVariantKey = (
  icp: IcpValue | null,
  product: IcpProductValue | null,
): `${IcpRegisterVariantIcp}.${IcpRegisterVariantProduct}` => {
  if (!icp) {
    if (product === IcpProduct.POS) return `${ICP_NO_ICP}.POS`
    if (product === IcpProduct.NOMINA) return `${ICP_NO_ICP}.NOMINA`
    if (product === IcpProduct.SF) return `${Icp.SMALL_BUSINESS}.SF`
    if (product === IcpProduct.AC) return `${Icp.MID_BUSINESS}.AC`
    return `${ICP_NO_ICP}.default`
  }

  if (product && isValidProductForIcp(icp, product)) {
    return `${icp}.${product}`
  }

  return `${icp}.default`
}

export const getIcpRegisterFlow = (location?: string): IcpRegisterFlow => {
  if (hasInvalidIcpRegisterQueryParams(location)) return 'invalid'

  return getIcpRegisterFlowFromParams(getIcpFromQuery(location), getProductFromQuery(location))
}

export const shouldSkipRedirectAfterRegister = (
  flow: IcpRegisterFlow,
  icp: IcpValue | null,
): boolean => {
  if (flow === 'default') return true
  if (flow === 'productOnly') {
    const product = getProductFromQuery()
    return product === IcpProduct.POS || product === IcpProduct.NOMINA
  }
  if (flow === 'withProduct') return false
  if (flow === 'withIcpOnly' && icp) {
    if (DIRECT_REDIRECT_ICPS.includes(icp)) return false
    if (RECOMMENDATION_ICPS.includes(icp)) return true
  }

  return false
}

export const getPostRegisterStep = (
  flow: IcpRegisterFlow,
  icp: IcpValue | null,
): IcpRegisterPendingStep | null => {
  if (flow === 'default') return ICP_REGISTER_STEP.CHOOSE_ICP
  if (flow === 'productOnly' && getProductFromQuery() === IcpProduct.POS) {
    return ICP_REGISTER_STEP.CHOOSE_ICP
  }
  if (flow === 'withIcpOnly' && icp && RECOMMENDATION_ICPS.includes(icp)) {
    return ICP_REGISTER_STEP.RECOMMENDATION
  }

  return null
}

/** ICPs que tras registro van directo al onboarding con icp + product (sin recomendación). */
export const getDirectIcpOnboardingProduct = (
  flow: IcpRegisterFlow,
  icp: IcpValue | null,
): IcpProductValue | null => {
  if (flow !== 'withIcpOnly' || !icp) return null
  if (icp === Icp.STUDENT || icp === Icp.ACCOUNTANT) return IcpProduct.AC
  return null
}

export const shouldShowRecommendationProgressHeader = (
  icp: IcpValue | null,
  flow?: IcpRegisterFlow,
): boolean => {
  const resolvedFlow = flow ?? getIcpRegisterFlow()
  if (resolvedFlow !== 'withIcpOnly' || !icp) return false
  return RECOMMENDATION_ICPS.includes(icp)
}
